import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  ExternalLink,
  FileText,
  KeyRound,
  Lock,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { canViewApiKeysSurface } from "../utils/apiSurfaceAccess";
import { getAuthHeader } from "../utils/auth";

const API_URL = window.location.hostname === "localhost"
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
  : window.location.origin;

const AGENT_WORKSPACE_ZIP_URL = `${process.env.PUBLIC_URL || "/app"}/downloads/salesbreaker-agent-workspace.zip`;
const CODEX_OFFICIAL_URL = "https://openai.com/codex/";

const WORKFLOWS = [
  {
    title: "Company settings and NG rules",
    mode: "API + browser assist",
    summary: "An AI agent can help prepare company profile data, sender details, NG lists, exclusion windows, and confirmation-ready changes.",
  },
  {
    title: "Templates and outreach copy",
    mode: "API + preview",
    summary: "An AI agent can draft, review, and refine message templates, including URL/CTA checks before save.",
  },
  {
    title: "Controlled recipient lists",
    mode: "Preview / review",
    summary: "An AI agent can review SalesBreaker-managed recipient lists, check counts and quality, and keep giant ID arrays out of the agent workspace.",
  },
  {
    title: "Campaign preview",
    mode: "Preview only",
    summary: "An AI agent can prepare a send plan and show count, target quality, template, quota, speed, and risk before a human decides.",
  },
  {
    title: "History, clicks, and engagement",
    mode: "Read / analyze",
    summary: "An AI agent can analyze send history, click logs, hot leads, and follow-up priorities from SalesBreaker data.",
  },
  {
    title: "Deals and CRM",
    mode: "Read + safe assist",
    summary: "An AI agent can read account context, summarize activity, prepare next actions, and help record follow-up notes.",
  },
  {
    title: "Leads, email, scenarios, forms, LPs, materials",
    mode: "Browser + API assist",
    summary: "An AI agent can review leads, AI traffic, access analytics, email drafts, scenarios, forms, LPs, and materials while asking for confirmation before publishing or sending.",
  },
];

const SAFETY_RULES = [
  "API keys resolve the tenant. Do not pass client_id in request bodies or query strings.",
  "Read and preview operations are preferred before any save, publish, delete, or send action.",
  "Saves, deletes, bulk changes, publishing, and sending require explicit human confirmation.",
  "The AI agent should not store raw API keys, Authorization headers, or large target ID arrays in logs or markdown files.",
  "Direct blind bulk sending is not available in this integration flow. Use target lists, previews, and approval first.",
];

const SETUP_STEPS = [
  ["1", "Create or choose an API key", "Use read/assist scopes for AI integration. API-key routes do not need a browser login, but they always require Bearer authentication."],
  ["2", "Learn about Codex if needed", "If you are new to OpenAI Codex, start with the official OpenAI overview."],
  ["3", "Download the workspace ZIP", "Unzip it locally. The root folder is JapanGtmAgentWorkspace/."],
  ["4", "Open the folder in your AI tool", "The agent should read AGENTS.md, START_HERE.md, and workspace-version.json before operating."],
  ["5", "Approve sensitive actions", "The agent should show the preview/diff/risk before saves, deletes, publishes, or sends."],
];

const TABS = [
  { id: "keys", label: "API keys", icon: KeyRound },
  { id: "docs", label: "Docs / Manual", icon: BookOpen },
  { id: "coverage", label: "AI coverage", icon: Sparkles },
];

const KEY_COLUMNS = ["Name", "Prefix", "Scopes", "Status", "Last used", "Actions"];

const ISSUE_SCOPE_OPTIONS = [
  {
    id: "crm.read",
    label: "CRM read",
    description: "Read send history, click events, and CRM-facing data for analysis and external CRM sync.",
  },
  {
    id: "quality.read",
    label: "List quality read",
    description: "Read company list quality scores, grades, recommended actions, and quality run metadata.",
  },
  {
    id: "operator.read",
    label: "SaaS Operator read",
    description: "Normal SaaS read bundle for approved users. Includes broader tenant read tools, web tracking, and AI discovery reads.",
  },
  {
    id: "operator.assist",
    label: "SaaS Operator assist",
    description: "Normal SaaS assist bundle. Includes the SaaS read bundle; My Lists still require the same paid/DNS/attestation unlock gate as the app screen.",
  },
  {
    id: "operator.read.directory_v1",
    label: "Directory read v1",
    description: "Directory-safe read/preview/planning bundle. It excludes My Lists, unrestricted company search, and send execution.",
  },
  {
    id: "operator.assist.directory_v1",
    label: "Directory assist v1",
    description: "Directory-safe assist bundle for setup, templates, and activity logging. It does not include list write scopes.",
  },
];

const PERMISSION_LABELS = {
  "crm.read": "CRM read",
  "quality.read": "List quality read",
  "operator.read": "SaaS Operator read",
  "operator.assist": "SaaS Operator assist",
  "operator.read.directory_v1": "Directory read v1",
  "operator.assist.directory_v1": "Directory assist v1",
};

const ERROR_LABELS = {
  api_key_list_failed: "Could not load API keys.",
  api_key_list_network_error: "Network error while loading API keys.",
  api_key_client_not_selected: "Select a client before managing API keys.",
  api_key_preview_failed: "Could not preview API key creation.",
  api_key_preview_network_error: "Network error while previewing key creation.",
  api_key_create_failed: "Could not create the API key.",
  api_key_create_network_error: "Network error while creating the API key.",
};

const permissionLabel = (permission) => PERMISSION_LABELS[permission] || permission;

const formatDateTime = (value) => {
  if (!value) return "Never used";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusTone = (status = "") => {
  if (status === "active") return "emerald";
  if (status === "disabled") return "amber";
  if (status === "revoked") return "rose";
  return "slate";
};

const messageForError = (code) => ERROR_LABELS[code] || code || "";

const StatusPill = ({ tone = "slate", children }) => {
  const classes = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${classes[tone] || classes.slate}`}>
      {children}
    </span>
  );
};

const SurfaceCard = ({ icon: Icon, title, description, children }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
          <Icon size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-950">{title}</h2>
          {description && <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>}
        </div>
      </div>
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

function ApiKeysEnPage() {
  const { user } = useOutletContext() || {};
  const canView = canViewApiKeysSurface(user);
  const selectedClientId = useMemo(() => {
    const role = user?.role;
    if (role === "admin" || role === "admin_staff") {
      return localStorage.getItem("selectedClientId") || user?.client_id || "";
    }
    return user?.client_id || "";
  }, [user]);

  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keyError, setKeyError] = useState("");
  const [isIssueOpen, setIsIssueOpen] = useState(false);
  const [issueForm, setIssueForm] = useState({
    name: "",
    description: "",
    scope_bundles: ["operator.read.directory_v1"],
    raw_key_display_once: false,
    store_key_securely: false,
  });
  const [issuePreview, setIssuePreview] = useState(null);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [issueLoading, setIssueLoading] = useState(false);
  const [copyState, setCopyState] = useState("");
  const [activeTab, setActiveTab] = useState("keys");

  const tenantLabel = selectedClientId || user?.client_id || "client not selected";

  const apiKeyHeaders = (json = false) => {
    const headers = { ...getAuthHeader(json) };
    if (selectedClientId && (user?.role === "admin" || user?.role === "admin_staff")) {
      headers["x-salesbreaker-client-id"] = selectedClientId;
    }
    return headers;
  };

  const fetchKeys = async () => {
    if (!canView) return;
    if (!selectedClientId) {
      setKeys([]);
      setKeyError("api_key_client_not_selected");
      return;
    }
    setKeysLoading(true);
    setKeyError("");
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys?limit=50`, {
        headers: apiKeyHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setKeys([]);
        setKeyError(data.error?.code || data.error || "api_key_list_failed");
        return;
      }
      setKeys(data.data?.keys || []);
    } catch (error) {
      setKeys([]);
      setKeyError("api_key_list_network_error");
    } finally {
      setKeysLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, selectedClientId]);

  const toggleIssueScope = (scope) => {
    setIssueForm((current) => {
      const hasScope = current.scope_bundles.includes(scope);
      const nextScopes = hasScope
        ? current.scope_bundles.filter((item) => item !== scope)
        : [...current.scope_bundles, scope];
      return {
        ...current,
        scope_bundles: nextScopes.length ? nextScopes : current.scope_bundles,
      };
    });
    setIssuePreview(null);
    setCreatedSecret(null);
  };

  const previewIssue = async () => {
    setIssueLoading(true);
    setKeyError("");
    setCreatedSecret(null);
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys/preview`, {
        method: "POST",
        headers: apiKeyHeaders(true),
        body: JSON.stringify({
          name: issueForm.name,
          description: issueForm.description,
          scope_bundles: issueForm.scope_bundles,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setIssuePreview(null);
        setKeyError(data.error?.code || data.error || "api_key_preview_failed");
        return;
      }
      setIssuePreview(data.data?.create_preview
        ? { ...data.data.create_preview, plan_gate: data.data.plan_gate || null }
        : null);
    } catch (error) {
      setIssuePreview(null);
      setKeyError("api_key_preview_network_error");
    } finally {
      setIssueLoading(false);
    }
  };

  const createIssue = async () => {
    setIssueLoading(true);
    setKeyError("");
    setCreatedSecret(null);
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys`, {
        method: "POST",
        headers: apiKeyHeaders(true),
        body: JSON.stringify({
          name: issueForm.name,
          description: issueForm.description,
          scope_bundles: issueForm.scope_bundles,
          acknowledgements: {
            raw_key_display_once: issueForm.raw_key_display_once,
            store_key_securely: issueForm.store_key_securely,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setCreatedSecret(null);
        setKeyError(data.error?.code || data.error || "api_key_create_failed");
        return;
      }
      setCreatedSecret(data.data?.secret || null);
      setIssuePreview(null);
      await fetchKeys();
    } catch (error) {
      setCreatedSecret(null);
      setKeyError("api_key_create_network_error");
    } finally {
      setIssueLoading(false);
    }
  };

  const changeKeyStatus = async (publicId, action) => {
    setKeyError("");
    try {
      const res = await fetch(`${API_URL}/api/developer/api-keys/${encodeURIComponent(publicId)}/${action}`, {
        method: "POST",
        headers: apiKeyHeaders(true),
        body: JSON.stringify({ reason: `${action} from private English API key page` }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        setKeyError(data.error?.code || data.error || `api_key_${action}_failed`);
        return;
      }
      await fetchKeys();
    } catch (error) {
      setKeyError(`api_key_${action}_network_error`);
    }
  };

  const copySecret = async () => {
    if (!createdSecret?.raw_key) return;
    try {
      await navigator.clipboard.writeText(createdSecret.raw_key);
      setCopyState("raw_key_copied");
      window.setTimeout(() => setCopyState(""), 1800);
    } catch (error) {
      setCopyState("copy_failed");
      window.setTimeout(() => setCopyState(""), 1800);
    }
  };

  if (!canView) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900">
        <div className="flex items-center gap-3">
          <Lock size={24} />
          <h1 className="text-xl font-bold">API keys are available to approved users</h1>
        </div>
        <p className="mt-3 text-sm leading-6">
          Only approved users can view SalesBreaker API keys and AI integration guidance.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap gap-2">
              <StatusPill tone="indigo">API keys</StatusPill>
              <StatusPill tone="amber">AI integration docs</StatusPill>
              <StatusPill tone="slate">Agent Workspace ZIP</StatusPill>
            </div>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950">
              API Keys / AI Integration
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Manage SalesBreaker API keys and share the right operating guidance with AI agents.
              Browser operations, API assist, and human confirmation are separated so Japan GTM workflows
              can be handled safely.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <div className="font-bold text-slate-900">Tenant resolved from</div>
            <div className="mt-1 font-mono text-xs">API key / login session</div>
            <div className="mt-3 font-bold text-slate-900">Current client</div>
            <div className="mt-1 font-mono text-xs">{tenantLabel}</div>
          </div>
        </div>
      </section>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <div className="flex min-w-max gap-2">
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-bold transition ${
                  active
                    ? "bg-slate-950 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === "keys" && (
        <div className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <SurfaceCard
              icon={KeyRound}
              title="API keys"
              description="Create Directory-safe keys for read/preview/planning, or normal SaaS keys for approved in-app workflows."
            >
              {keyError && (
                <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                  {messageForError(keyError)}
                </div>
              )}
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-slate-600">
                  Raw API keys are displayed once. SalesBreaker stores only a hash and key metadata.
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsIssueOpen(true);
                    setIssuePreview(null);
                    setCreatedSecret(null);
                    setKeyError("");
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"
                >
                  <KeyRound size={16} />
                  Create API key
                </button>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                    <tr>
                      {KEY_COLUMNS.map((column) => (
                        <th key={column} className="px-4 py-3">{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {keysLoading && (
                      <tr>
                        <td colSpan={KEY_COLUMNS.length} className="px-4 py-8 text-center text-sm text-slate-600">
                          Loading API keys...
                        </td>
                      </tr>
                    )}
                    {!keysLoading && keys.length === 0 && (
                      <tr>
                        <td colSpan={KEY_COLUMNS.length} className="px-4 py-10 text-center">
                          <div className="mx-auto max-w-xl">
                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                              <KeyRound size={24} />
                            </div>
                            <h3 className="mt-4 text-base font-bold text-slate-900">No API keys yet</h3>
                            <p className="mt-2 text-sm leading-6 text-slate-600">
                              Create an API key to show the raw key once. Store it securely before closing the dialog.
                            </p>
                            <button
                              type="button"
                              onClick={() => setIsIssueOpen(true)}
                              className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-950 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
                            >
                              <KeyRound size={16} />
                              Create API key
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!keysLoading && keys.map((key) => (
                      <tr key={key.public_id || key.id} className="align-top">
                        <td className="px-4 py-4">
                          <div className="font-bold text-slate-900">{key.name}</div>
                          {key.description && (
                            <div className="mt-1 max-w-xs text-xs leading-5 text-slate-500">{key.description}</div>
                          )}
                          <div className="mt-2 font-mono text-xs text-slate-500">{key.public_id}</div>
                        </td>
                        <td className="px-4 py-4 font-mono text-xs text-slate-600">{key.key_prefix}</td>
                        <td className="px-4 py-4">
                          <div className="flex max-w-xs flex-wrap gap-1.5">
                            {(key.scope_bundles || []).map((scope) => (
                              <StatusPill key={scope} tone="indigo">{permissionLabel(scope)}</StatusPill>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <StatusPill tone={statusTone(key.status)}>{key.status}</StatusPill>
                        </td>
                        <td className="px-4 py-4 text-xs leading-5 text-slate-600">{formatDateTime(key.last_used_at)}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-2">
                            {key.status === "active" && (
                              <button
                                type="button"
                                onClick={() => changeKeyStatus(key.public_id, "disable")}
                                className="rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-50"
                              >
                                Disable
                              </button>
                            )}
                            {key.status === "disabled" && (
                              <button
                                type="button"
                                onClick={() => changeKeyStatus(key.public_id, "enable")}
                                className="rounded-md border border-emerald-300 px-2.5 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
                              >
                                Enable
                              </button>
                            )}
                            {key.status !== "revoked" && (
                              <button
                                type="button"
                                onClick={() => changeKeyStatus(key.public_id, "revoke")}
                                className="rounded-md border border-rose-300 px-2.5 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-50"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SurfaceCard>

            <SurfaceCard
              icon={ShieldCheck}
              title="API key lifecycle"
              description="Manage preview, creation, temporary disablement, and revocation from this screen."
            >
              <div className="space-y-3">
                {[
                  ["1", "Preview before creation", "Confirm scopes and plan gates before issuing the key."],
                  ["2", "Create API key", "The raw key is displayed once and cannot be recovered by SalesBreaker."],
                  ["3", "Use with external tools", "Use it for CRM sync, list quality checks, and AI-assisted operations."],
                  ["4", "Review usage", "Check status, last used time, and metadata."],
                  ["5", "Disable or revoke", "Disable temporarily or revoke permanently when needed."],
                ].map(([number, title, text]) => (
                  <div key={number} className="flex gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-bold text-white">
                      {number}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-900">{title}</div>
                      <p className="mt-1 text-xs leading-5 text-slate-600">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </SurfaceCard>
          </div>
        </div>
      )}

      {activeTab === "docs" && (
        <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
          <SurfaceCard
            icon={BookOpen}
            title="Docs / Agent Workspace"
            description="The downloaded workspace contains the current operating manual and JSON contracts."
          >
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-white p-2 text-slate-700 shadow-sm">
                  <Sparkles size={18} />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-950">Prepare to use Codex</div>
                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    If you are new to Codex, start with the official OpenAI overview. SalesBreaker provides
                    the Agent Workspace ZIP so your AI tool can read the operating rules and API contracts.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <a
                  href={CODEX_OFFICIAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 hover:bg-slate-100"
                >
                  <ExternalLink size={16} />
                  Learn about OpenAI Codex
                </a>
                <a
                  href={AGENT_WORKSPACE_ZIP_URL}
                  download
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"
                >
                  <Download size={16} />
                  Download Agent Workspace ZIP
                </a>
              </div>
            </div>
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-bold text-slate-900">Agent setup steps</div>
              <ol className="mt-2 space-y-1 text-xs leading-5 text-slate-600">
                {SETUP_STEPS.map(([number, title, text]) => (
                  <li key={number}>{number}. {title}: {text}</li>
                ))}
              </ol>
            </div>
            <div className="grid gap-3 text-sm">
              {[
                ["AGENTS.md", "AI-agent workspace rules and safety expectations."],
                ["START_HERE.md", "First-run setup, freshness checks, and how to start a SalesBreaker task."],
                ["docs/salesbreaker-agent-contract-pack.md", "Screen-by-screen JSON contracts and API/browser assist guidance."],
                ["docs/agent-frontend-coverage.md", "Full SalesBreaker feature coverage map."],
                ["workspace-version.json", "Latest workspace identity and freshness marker."],
              ].map(([file, description]) => (
                <div key={file} className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="font-mono text-xs font-bold text-slate-950">{file}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-600">{description}</div>
                </div>
              ))}
            </div>
          </SurfaceCard>

          <SurfaceCard
            icon={FileText}
            title="Example AI-agent instruction"
            description="Give the agent a business goal. The workspace explains how to choose API assist or browser assist."
          >
            <pre className="overflow-x-auto rounded-lg bg-slate-950 p-4 text-xs leading-6 text-slate-100">
              <code>{`Open this folder in my AI coding tool.
Read AGENTS.md and START_HERE.md first.
Use my SalesBreaker API key only for approved read/assist routes.

Goal:
Build a target list for Japanese B2B companies,
draft an outreach template,
show me the campaign preview,
and prepare CRM follow-up actions.

Do not send, publish, delete, or bulk-change anything
without my confirmation.`}</code>
            </pre>
          </SurfaceCard>
        </div>
      )}

      {activeTab === "coverage" && (
        <div className="space-y-6">
          <SurfaceCard
            icon={Sparkles}
            title="What AI agents can assist with"
            description="This page is an API and AI integration guide for approved SalesBreaker users, not a public marketplace listing."
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {WORKFLOWS.map((workflow) => (
                <div key={workflow.title} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-sm font-bold text-slate-950">{workflow.title}</h3>
                    <StatusPill tone="indigo">{workflow.mode}</StatusPill>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{workflow.summary}</p>
                </div>
              ))}
            </div>
          </SurfaceCard>

          <SurfaceCard
            icon={ShieldCheck}
            title="Safety model"
            description="The workspace is designed for safe AI-assisted operation, not blind automation."
          >
            <ul className="space-y-3 text-sm leading-6 text-slate-700">
              {SAFETY_RULES.map((rule) => (
                <li key={rule} className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" size={18} />
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </SurfaceCard>
        </div>
      )}

      {isIssueOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-950">Create API key</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Use this key with an approved AI integration after downloading the latest Agent Workspace ZIP. API-key routes work without a browser session, but never without Bearer authentication.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsIssueOpen(false)}
                className="rounded-md px-3 py-1.5 text-sm font-bold text-slate-500 hover:bg-slate-100"
              >
                Close
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-sm font-bold text-slate-800">Key name</span>
                <input
                  value={issueForm.name}
                  onChange={(event) => setIssueForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="AI integration key"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>
              <label className="block">
                <span className="text-sm font-bold text-slate-800">Description</span>
                <textarea
                  value={issueForm.description}
                  onChange={(event) => setIssueForm((current) => ({ ...current, description: event.target.value }))}
                  placeholder="Used with the JAPAN GTM Agent Workspace."
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </label>

              <div>
                <div className="text-sm font-bold text-slate-800">Scopes</div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {ISSUE_SCOPE_OPTIONS.map((scope) => {
                    const checked = issueForm.scope_bundles.includes(scope.id);
                    return (
                      <label
                        key={scope.id}
                        className={`cursor-pointer rounded-lg border p-3 transition ${
                          checked ? "border-indigo-300 bg-indigo-50" : "border-slate-200 bg-white hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleIssueScope(scope.id)}
                          className="mr-2"
                        />
                        <span className="text-sm font-bold text-slate-950">{scope.label}</span>
                        <p className="mt-1 text-xs leading-5 text-slate-600">{scope.description}</p>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                <div className="flex gap-2 font-bold">
                  <AlertTriangle size={18} />
                  Required acknowledgement
                </div>
                <label className="mt-3 flex gap-2">
                  <input
                    type="checkbox"
                    checked={issueForm.raw_key_display_once}
                    onChange={(event) => setIssueForm((current) => ({ ...current, raw_key_display_once: event.target.checked }))}
                  />
                  <span>I understand the raw API key is displayed once and cannot be recovered.</span>
                </label>
                <label className="mt-2 flex gap-2">
                  <input
                    type="checkbox"
                    checked={issueForm.store_key_securely}
                    onChange={(event) => setIssueForm((current) => ({ ...current, store_key_securely: event.target.checked }))}
                  />
                  <span>I will store the key securely and never put it in URLs, public repositories, frontend code, AI-agent logs, or markdown files.</span>
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={previewIssue}
                  disabled={issueLoading || !issueForm.name.trim()}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Preview creation
                </button>
                <button
                  type="button"
                  onClick={createIssue}
                  disabled={
                    issueLoading ||
                    !issueForm.name.trim() ||
                    !issueForm.raw_key_display_once ||
                    !issueForm.store_key_securely
                  }
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  Create key
                </button>
              </div>

              {issuePreview && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm leading-6 text-indigo-950">
                  <div className="font-bold">Creation preview</div>
                  <div className="mt-2">Scopes: {(issuePreview.scope_bundles || []).map(permissionLabel).join(" / ")}</div>
                  {issuePreview.plan_gate && (
                    <div>Plan gate: {issuePreview.plan_gate.allowed ? "allowed" : "blocked"}</div>
                  )}
                  <div>Raw key display: once only</div>
                  <div>Stored value: hash only</div>
                </div>
              )}

              {createdSecret?.raw_key && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-bold text-emerald-900">
                    <CheckCircle2 size={18} />
                    API key created. Copy it now.
                  </div>
                  <div className="mt-3 rounded-lg bg-white p-3 font-mono text-xs text-slate-900">
                    {createdSecret.raw_key}
                  </div>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-500"
                  >
                    <Copy size={16} />
                    {copyState === "raw_key_copied" ? "Copied" : "Copy raw key"}
                  </button>
                  <p className="mt-3 text-xs leading-5 text-emerald-900">
                    After closing this dialog, the raw key cannot be displayed again.
                  </p>
                </div>
              )}

              {issueLoading && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock size={16} />
                  Processing...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiKeysEnPage;
